const DEV_ROLE_KEY = 'fcp-dev-user-role';
const DEV_ASSUME_USER_KEY = 'fcp-dev-assume-user-id';

/*
 * 🛑 sessionStorage FIRST, localStorage AS A FALLBACK — and the fallback exists for a
 *    measured reason, not for tidiness.
 *
 *    The dev role lives in sessionStorage, which is per-context and is NOT carried by
 *    Playwright's `storageState` (that covers cookies and localStorage only). So every
 *    fresh browser context started by the E2E suite had no role, landed on DevLoginPage,
 *    and found no navigation sidebar — which is why 37 navigation tests and much of the
 *    rest of the suite failed while the application itself was working perfectly.
 *
 *    Reading localStorage as a fallback lets the harness seed a role once, for every
 *    context, without a per-test fixture. Writes still go to sessionStorage so a human's
 *    role choice stays scoped to their tab.
 */
export function getDevUserRole(): string | null {
  return sessionStorage.getItem(DEV_ROLE_KEY) ?? localStorage.getItem(DEV_ROLE_KEY);
}

export function setDevUserRole(role: string): void {
  sessionStorage.setItem(DEV_ROLE_KEY, role);
}

export function clearDevUserRole(): void {
  sessionStorage.removeItem(DEV_ROLE_KEY);
}

/**
 * The stored dev role as the X-Dev-User-Role header the backend's DEV_AUTH_BYPASS reads (it
 * defaults to Admin without it). Read at call time so a role switch takes effect on the next
 * request. 🛑 AuthProvider fetched /api/auth/session WITHOUT it, so every session came back Admin
 * and overwrote the picked role — the dev role switch could never leave Admin.
 */
export function devRoleHeaders(): Record<string, string> {
  const role = getDevUserRole();
  return role ? { 'X-Dev-User-Role': role } : {};
}

// Spec 255 (dev testing): the user identity to impersonate for the multi-user alert flow.
// Sent as X-Dev-Assume-User-Id; the backend (non-prod only) overrides req.user with it.
export function getDevAssumeUserId(): string | null {
  return sessionStorage.getItem(DEV_ASSUME_USER_KEY);
}

export function setDevAssumeUserId(id: string): void {
  sessionStorage.setItem(DEV_ASSUME_USER_KEY, id);
}

export function clearDevAssumeUserId(): void {
  sessionStorage.removeItem(DEV_ASSUME_USER_KEY);
}

/*
 * 🛑 THE ASSUMED TENANT, WITHOUT WHICH THE ASSUMED USER CANNOT BE FOUND.
 *
 *    Spec 241: `userRepository.getById` is tenant-scoped, so the backend's dev switcher looks
 *    the target up INSIDE a bound tenant. Assuming a user who belongs to another tenant
 *    therefore needs that tenant supplied — the backend reads `x-dev-assume-tenant-id` (or the
 *    matching cookie) and binds it for the lookup.
 *
 * 🛑 THE BACKEND HALF SHIPPED THIS MORNING AND THIS HALF DID NOT, WHICH MADE THE WHOLE FEATURE
 *    UNUSABLE FROM THE UI WHILE PASSING EVERY CHECK I RAN.
 *
 *    I verified the switcher with `curl`, where I set the cookie by hand, and concluded the
 *    read path was unblocked. Through the actual browser it never worked: `apiClient` forwards
 *    `X-Dev-Assume-User-Id` and nothing else, so the tenant never reached the server.
 *
 *    The failure was invisible because auth and data take DIFFERENT ROUTES in dev. Auth goes to
 *    `localhost:3000/api/...` through the Vite proxy — same-origin, so the hand-set cookie WAS
 *    sent and the backend really did assume the tenant-2 user. Data goes direct to
 *    `localhost:3001` — cross-origin, and `apiClient` sends no credentials, so those requests
 *    carried tenant 1. The screen therefore rendered a TENANT-2 IDENTITY BESIDE TENANT-1 DATA,
 *    which reads exactly like a cross-tenant leak and is not one.
 *
 *    Found only by opening a browser and reading the screen. Every API-level probe passed,
 *    because at the API level everything was correct.
 *
 *    Same sessionStorage-then-localStorage rule as the role above, for the same Playwright
 *    `storageState` reason — the E2E suite must be able to seed a tenant once per context.
 */
const DEV_ASSUME_TENANT_KEY = 'fcp-dev-assume-tenant-id';

export function getDevAssumeTenantId(): string | null {
  return sessionStorage.getItem(DEV_ASSUME_TENANT_KEY) ?? localStorage.getItem(DEV_ASSUME_TENANT_KEY);
}

export function setDevAssumeTenantId(id: string): void {
  sessionStorage.setItem(DEV_ASSUME_TENANT_KEY, id);
}

export function clearDevAssumeTenantId(): void {
  sessionStorage.removeItem(DEV_ASSUME_TENANT_KEY);
}
